import React from 'react';

import { useAppSelector } from '@fd/customHooks/useAppSelector';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getTranslate } from 'react-localize-redux';
import { connect } from 'react-redux';

import PaperContainer from '@fd/ui/Layout/PaperContainer';

import { FIVE_MINUTES } from '../../../../../../../helpers/timeConstants';
import { notifyError, NotifyProps, notifySaved } from '../../../../../../../layouts/Notify/actions';
import { BuildRequest, whitelabelService } from '../../../../whitelabel.service';
import { BuildSection } from './components';

type Props = MappedState & MappedDispatch;

const BuildTab = ({ dispatchNotifyError, dispatchNotifySaved }: Props) => {
  const currentApp = useAppSelector((state) => state.currentApp);
  const whiteLabelId = currentApp?.AppId || '';

  // Query for whitelabel config
  const {
    data: whitelabelConfig,
    isLoading: isLoadingConfig,
    error: configError,
  } = useQuery({
    queryKey: ['whitelabelConfig', whiteLabelId],
    queryFn: () => whitelabelService.getWhiteLabelConfig(whiteLabelId),
    enabled: !!whiteLabelId,
    staleTime: FIVE_MINUTES,
  });

  // Mutation for iOS builds
  const { mutate: startIosBuild, isPending: isIosLoading } = useMutation({
    mutationFn: (buildRequest: BuildRequest) =>
      whitelabelService.startIosBuild(whiteLabelId, buildRequest),
    onSuccess: () => {
      dispatchNotifySaved();
    },
    onError: (error: any) => {
      dispatchNotifyError({
        message: error.message || 'Failed to start iOS build',
        translate: true,
      });
    },
  });

  // Mutation for Android builds
  const { mutate: startAndroidBuild, isPending: isAndroidLoading } = useMutation({
    mutationFn: (buildRequest: BuildRequest) =>
      whitelabelService.startAndroidBuild(whiteLabelId, buildRequest),
    onSuccess: () => {
      dispatchNotifySaved();
    },
    onError: (error: any) => {
      dispatchNotifyError({
        message: error.message || 'Failed to start Android build',
        translate: true,
      });
    },
  });

  const handleStartBuild = async (platform: 'iOS' | 'Android', buildRequest: BuildRequest) => {
    if (!whiteLabelId) {
      dispatchNotifyError({ message: 'WhiteLabel ID not found', translate: true });
      return;
    }

    if (platform === 'iOS') {
      startIosBuild(buildRequest);
    } else {
      startAndroidBuild(buildRequest);
    }
  };

  if (isLoadingConfig) {
    return (
      <PaperContainer fluid>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      </PaperContainer>
    );
  }

  return (
    <PaperContainer fluid>
      <Grid container spacing={3} sx={{ padding: '24px' }}>
        <Grid item xs={12}>
          <Typography variant="h6" gutterBottom>
            Start Builds
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Start builds for iOS and Android apps
          </Typography>
        </Grid>

        {/* Config Error Message */}
        {configError && (
          <Grid item xs={12}>
            <Alert severity="error">Failed to load whitelabel configuration</Alert>
          </Grid>
        )}

        {/* Whitelabel Generic Info */}
        {whitelabelConfig && (
          <Grid item xs={12}>
            <Card>
              <CardHeader title={<Typography variant="h6">Whitelabel Configuration</Typography>} />
              <CardContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      App Name
                    </Typography>
                    <Typography variant="body1">
                      {whitelabelConfig.AppStoreName || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      Bundle ID
                    </Typography>
                    <Typography variant="body1">
                      ie.flipdish.{whitelabelConfig.AppNameId || 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* iOS Build Section */}
        <Grid item xs={12} md={6}>
          <BuildSection
            platform="iOS"
            onStartBuild={(buildRequest) => handleStartBuild('iOS', buildRequest)}
            isLoading={isIosLoading}
          />
        </Grid>

        {/* Android Build Section */}
        <Grid item xs={12} md={6}>
          <BuildSection
            platform="Android"
            onStartBuild={(buildRequest) => handleStartBuild('Android', buildRequest)}
            isLoading={isAndroidLoading}
          />
        </Grid>
      </Grid>
    </PaperContainer>
  );
};

type MappedState = ReturnType<typeof mapStateToProps>;
const mapStateToProps = (state: AppState) => ({
  translate: getTranslate(state),
});

type MappedDispatch = ReturnType<typeof mapDispatchToProps>;
const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  dispatchNotifyError: (data: NotifyProps) => dispatch(notifyError(data)),
  dispatchNotifySaved: () => dispatch(notifySaved()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BuildTab);
